import { useEffect, useMemo, useState } from 'react'
import useFeed from '../../lib/useFeed'
import useGroup from '../../lib/useGroup'
import useUser from '../../lib/useUser'
import { useTranslation } from '../../lib/i18n'
import HomePageEmpty from '../../public/lottie/HomePageEmpty.json'
import ClientOnlyLottie from '../common/ClientOnlyLottie'
import Container from '../common/Container'
import EmptyView from '../common/EmptyView'
import PaginatedWithButton from '../common/pagination/PaginatedWithButton'
import { useCacheKey } from '../context/CacheKey'
import CreateActivityButton from './CreateActivityButton'
import EventFilters from './Feed/EventFilters'
import FeedOverlay from './Feed/FeedOverlay'
import GroupFilters from './Feed/GroupFilters'
import Post from './Post'
import Event from './event/Event'

function Feed({
  filters,
  itemType,
  showButton,
  postInType,
  postInId,
  postInSlug,
  emptyAccessory = null,
  detailedActivityButton = false,
  modalVisible = false,
  EmptyOverrideComponent = null,
}) {
  const { t } = useTranslation()
  const descriptor = useMemo(
    () => (itemType == null ? 'Activity' : itemType),
    [itemType]
  )
  const descriptorPlural = useMemo(
    () => (itemType == null ? descriptor : `${itemType}s`),
    [itemType, descriptor]
  )
  const groupSlug = useMemo(
    () => (postInType === 'Group' ? postInSlug : null),
    [postInType, postInSlug]
  )
  const { group } = useGroup(groupSlug)
  const showGroupFilters = useMemo(
    () => itemType !== 'Event' && postInType === 'Group',
    [itemType, postInType]
  )
  const showEventFilters = useMemo(() => itemType === 'Event', [itemType])
  const [selectedFilters, setSelectedFilters] = useState(
    itemType === 'Event' ? { upcoming: true } : {}
  )
  const feedFilters = useMemo(() => {
    const baseFilters =
      itemType != null ? { ...filters, item_type: itemType } : { ...filters }
    return {
      ...baseFilters,
      ...selectedFilters,
    }
  }, [filters, itemType, selectedFilters])

  const {
    items,
    cacheKey,
    isLoadingInitialData,
    isLoadingMore,
    isReachingEnd,
    loadMore,
  } = useFeed(feedFilters)
  const { user } = useUser()
  const cacheKeyContext = useCacheKey()

  useEffect(() => {
    cacheKeyContext.setCacheKey(cacheKey)
  }, [cacheKey, cacheKeyContext])

  const emptyView = useMemo(() => {
    if (EmptyOverrideComponent) return <EmptyOverrideComponent />
    return (
      <EmptyView
        title={t('feed.noItemsToShow', {
          itemType: descriptorPlural?.toLowerCase(),
        })}
        instructions={t('feed.emptyInstructionsList')}
        accessory={
          emptyAccessory || (
            <ClientOnlyLottie
              loop
              animationData={HomePageEmpty}
              play
              style={{ width: 350, height: 350 }}
            />
          )
        }
      />
    )
  }, [EmptyOverrideComponent, t, descriptorPlural, emptyAccessory])

  if (isLoadingInitialData && (!items || items.length <= 0)) {
    return (
      <Container className="mx-auto px-0">
        <div className="flex flex-col max-w-700 mx-auto">
          {showButton && user && (
            <CreateActivityButton
              postInType={postInType}
              postInId={postInId}
              postInSlug={postInSlug}
              itemType={itemType}
              detailedActivityButton={detailedActivityButton}
              modalVisible={modalVisible}
            />
          )}
          <FeedOverlay hideMessage />
        </div>
      </Container>
    )
  }

  return (
    <Container className="mx-auto px-0">
      <div className="flex flex-col max-w-700 mx-auto">
        {showGroupFilters && (
          <GroupFilters
            selectedFilters={selectedFilters}
            setSelectedFilters={setSelectedFilters}
            group={group}
          />
        )}
        {showEventFilters && (
          <EventFilters
            selectedFilters={selectedFilters}
            setSelectedFilters={setSelectedFilters}
          />
        )}
        {showButton && user && (
          <CreateActivityButton
            postInType={postInType}
            postInId={postInId}
            postInSlug={postInSlug}
            itemType={itemType}
            detailedActivityButton={detailedActivityButton}
            modalVisible={modalVisible}
          />
        )}
        <div className="flex flex-col items-center">
          <PaginatedWithButton
            isLoadingMore={isLoadingMore}
            isLoadingInitialData={isLoadingInitialData}
            isReachingEnd={isReachingEnd}
            loadMore={loadMore}
            key={JSON.stringify(selectedFilters)}
            EmptyComponent={emptyView}
          >
            {items.map((item) =>
              item.type === 'Event' || itemType === 'Event' ? (
                <Event
                  event={item}
                  key={`event-${item.id}`}
                  postInId={postInId}
                  archived={group?.archived}
                />
              ) : (
                <Post
                  post={item}
                  key={`post-${item.id}`}
                  postInId={postInId}
                  archived={group?.archived}
                />
              )
            )}
          </PaginatedWithButton>
        </div>
      </div>
    </Container>
  )
}

export default Feed
